import { normalizeScriptarioLocale } from "@/config/locales";

const appBaseUrl = process.env.NEXT_PUBLIC_APP_BASE_URL?.trim() || "";
const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || "";
const appEnv = process.env.NEXT_PUBLIC_APP_ENV?.trim() || "production";
const siteIdValue = process.env.NEXT_PUBLIC_SITE_ID?.trim() || process.env.NEXT_PUBLIC_SITE_MARKET?.trim() || "transcripta";
const siteLocaleValue = process.env.NEXT_PUBLIC_LOCALE?.trim() || process.env.NEXT_PUBLIC_SITE_LOCALE?.trim() || (siteIdValue === "scriptario" ? "en" : "ru");
const yandexMetrikaIdValue = process.env.NEXT_PUBLIC_YANDEX_METRIKA_ID?.trim() || "";
const googleAdsIdValue = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID?.trim() || "";
const googleAnalyticsIdValue = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim() || "";
const googleTagManagerIdValue = process.env.NEXT_PUBLIC_GTM_ID?.trim() || "";
const tamsenseApiKeyValue = process.env.NEXT_PUBLIC_TAMSENSE_API_KEY?.trim() || "";
const tamsenseFeatureValue = process.env.NEXT_PUBLIC_FEATURE_TAMSENSE?.trim() === "true";
const faroEnabledValue = process.env.NEXT_PUBLIC_FARO_ENABLED?.trim() === "true";
const faroUrlValue = process.env.NEXT_PUBLIC_FARO_URL?.trim() || "";
const faroReleaseValue = process.env.NEXT_PUBLIC_FARO_RELEASE?.trim() || "";
const faroSampleRateValue = Number(process.env.NEXT_PUBLIC_FARO_SAMPLE_RATE?.trim() || "1");
const attributionSiteIdValue = process.env.NEXT_PUBLIC_ATTRIBUTION_SITE_ID?.trim() || "";

// Guest transcription — toggled via NEXT_PUBLIC_FEATURE_GUEST_TRANSCRIPTION.
// When unset, satellite markets that rely on guest-first onboarding keep it on
// by default, while the core Transcripta landing keeps the historical register
// funnel.
const guestTranscriptionFlagValue = process.env.NEXT_PUBLIC_FEATURE_GUEST_TRANSCRIPTION?.trim();

function normalizeBaseUrl(value: string, fallback: string) {
  if (!value) return fallback;
  return value.replace(/\/+$/, "");
}

type AppUrlParamValue = string | number | boolean | null | undefined;

function joinUrl(base: string, path: string) {
  const normalizedBase = normalizeBaseUrl(base, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return normalizedBase ? `${normalizedBase}${normalizedPath}` : normalizedPath;
}

function appendQueryParams(url: string, params: Record<string, AppUrlParamValue> = {}) {
  const isAbsolute = /^https?:\/\//i.test(url);
  const parsed = new URL(url, isAbsolute ? undefined : "https://scriptario.local");

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    parsed.searchParams.set(key, String(value));
  });

  if (isAbsolute) return parsed.toString();
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export const siteUrl = normalizeBaseUrl(appBaseUrl, "http://localhost:3001");
export const landingUrl = siteUrl.replace("://app.", "://").replace(/\/app$/, "");
export const publicApiBaseUrl = normalizeBaseUrl(apiBaseUrl, "");
export const publicAppEnv = appEnv;
export const siteId = siteIdValue;
export const siteMarket = siteIdValue;
export const siteLocale = siteLocaleValue;
export const isScriptario = siteId === "scriptario";
export const isAudio2Text = siteId === "audio2text";
export const yandexMetrikaId = yandexMetrikaIdValue ? Number(yandexMetrikaIdValue) : 0;
export const googleAdsId = googleAdsIdValue;
export const googleAnalyticsId = isScriptario ? googleAnalyticsIdValue : "";
export const googleTagManagerId = isScriptario ? googleTagManagerIdValue : "";
export const tamsenseApiKey = isScriptario && tamsenseFeatureValue ? tamsenseApiKeyValue : "";
export const featureTamsense = isScriptario && tamsenseFeatureValue;
export const scriptarioAnalyticsMode = googleTagManagerId
  ? "gtm"
  : googleAnalyticsId
    ? "ga4"
    : "disabled";
export const featureGuestTranscription = guestTranscriptionFlagValue
  ? guestTranscriptionFlagValue === "true"
  : (isScriptario || isAudio2Text);
export const faroEnabled = faroEnabledValue;
export const faroUrl = faroUrlValue;
export const faroRelease = faroReleaseValue;
export const faroSampleRate = Number.isFinite(faroSampleRateValue)
  ? Math.min(1, Math.max(0, faroSampleRateValue))
  : 1;

// Allowlist of Audio2Text mirror attribution site ids. An unlisted value falls
// back to the canonical "audio2text", which silently collapses per-mirror
// attribution: the landing CTA then hands off to /app with site_id=audio2text,
// so registrations and payments from that mirror are recorded against the
// canonical counter instead of the mirror's own one.
//
// Keep in sync with the mirror inventory — the deploy workflow variants,
// NEXT_PUBLIC_INTERNAL_ATTRIBUTION_DOMAINS in this repo, the frontend
// AUDIO2TEXT_HOST_SITE_IDS map and the backend market map.
const AUDIO2TEXT_ATTRIBUTION_SITE_ID_RE =
  /^(audio-2-?text|audio-2-text-(?:ai|app)|audio2-text|audio2text-(?:pro|app|ai))\.(ru|online)$/;
const TRANSCRIPTA_ATTRIBUTION_SITE_IDS = new Set([
  "transcripta-app.ru",
  "transcripta-app.online",
  "transcripta-ai.ru",
  "transcripta-ai.online",
  "transcripta-pro.ru",
]);

function normalizeAudio2TextAttributionSiteId(value: string | null | undefined) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "audio2text") return normalized;
  return AUDIO2TEXT_ATTRIBUTION_SITE_ID_RE.test(normalized) ? normalized : null;
}

export function getAudio2TextAttributionSiteId() {
  if (!isAudio2Text) return siteId;

  const fromEnv = normalizeAudio2TextAttributionSiteId(attributionSiteIdValue);
  if (fromEnv) return fromEnv;

  return "audio2text";
}

export function getTranscriptaAttributionSiteId() {
  if (siteId !== "transcripta") return siteId;
  const normalized = attributionSiteIdValue.toLowerCase();
  return TRANSCRIPTA_ATTRIBUTION_SITE_IDS.has(normalized) ? normalized : "transcripta";
}

export function getYandexMetrikaId() {
  return yandexMetrikaId;
}

export function buildAppUrl(path: string, params: Record<string, AppUrlParamValue> = {}) {
  const normalizedBase = normalizeBaseUrl(siteUrl, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const baseHasAppPrefix = /\/app$/.test(normalizedBase);
  const appPath = normalizedPath.replace(/^\/app(?=\/|$)/, "") || "/";
  const appBase = baseHasAppPrefix ? normalizedBase : joinUrl(normalizedBase, "/app");
  const appUrl = joinUrl(appBase, appPath);

  if (!isScriptario) {
    const siteParams = isAudio2Text
      ? { site_id: getAudio2TextAttributionSiteId() }
      : siteId === "transcripta"
        ? { site_id: getTranscriptaAttributionSiteId() }
        : {};
    return appendQueryParams(appUrl, { ...siteParams, ...params });
  }

  const normalizedLocale = normalizeScriptarioLocale(siteLocale);
  return appendQueryParams(appUrl, { locale: normalizedLocale, ...params });
}

export function buildApiUrl(path: string) {
  if (!publicApiBaseUrl) return path.startsWith("/") ? path : `/${path}`;
  return joinUrl(publicApiBaseUrl, path);
}
